import { X, Camera } from 'lucide-react';

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = "jmiseller";
const CLOUDINARY_API_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

const validateSquareImage = (file) => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve({ valid: false, file, error: `"${file.name}" is not an image file.` });
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const ratio = width / height;
      // Allow 1% margin of error for minor rounding
      const isOneToOne = Math.abs(ratio - 1) <= 0.01;

      if (isOneToOne) {
        resolve({ valid: true, file, width, height });
      } else {
        resolve({
          valid: false,
          file,
          width,
          height,
          error: `"${file.name}" is ${width}x${height} (${ratio.toFixed(2)}:1). Only 1:1 (square) images are allowed.`,
        });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ valid: false, file, error: `Failed to load image "${file.name}".` });
    };

    img.src = objectUrl;
  });
};

const ImageUpload = ({ uploadedImages, setUploadedImages, mainImageIndex, setMainImageIndex, uploading, setUploading }) => {
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileList = Array.from(files);
    const validations = await Promise.all(fileList.map(validateSquareImage));
    const invalid = validations.filter((v) => !v.valid);
    const valid = validations.filter((v) => v.valid);

    // Reset input so re-selecting the same file works
    e.target.value = '';

    if (invalid.length > 0) {
      const errorList = invalid.map((item) => `• ${item.error}`).join('\n');
      alert(`Only 1:1 (square) images are allowed!\n\n${errorList}`);
    }

    if (valid.length === 0) {
      return;
    }

    setUploading(true);
    const uploadPromises = valid.map(async ({ file }) => {
      const formDataCloud = new FormData();
      formDataCloud.append('file', file);
      formDataCloud.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      try {
        const response = await fetch(CLOUDINARY_API_URL, { method: 'POST', body: formDataCloud });
        const data = await response.json();
        return { id: data.public_id, url: data.secure_url, name: file.name };
      } catch (error) {
        console.error('Upload failed:', error);
        return null;
      }
    });
    const results = await Promise.all(uploadPromises);
    const successfulUploads = results.filter(Boolean);
    setUploadedImages(prev => [...prev, ...successfulUploads]);
    setUploading(false);
  };

  const removeImage = (id) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
    if (mainImageIndex >= uploadedImages.length - 1) {
      setMainImageIndex(0);
    }
  };

  return (
    <div className="upload-section">
      <h3 className="section-title">
        Upload Photos <span style={{ color: '#e53e3e' }}>*</span>
        <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#6b7280', marginLeft: '8px' }}>
          (1:1 Square ratio only)
        </span>
      </h3>
      {uploadedImages.length > 0 ? (
        <div className="image-upload-container">
          <div className="main-image-container">
            <img src={uploadedImages[mainImageIndex]?.url} alt="Main" className="main-image" />
          </div>
          <div className="thumbnail-grid">
            {uploadedImages.map((image, index) => (
              <div key={image.id} className={`thumbnail-container ${index === mainImageIndex ? 'active' : ''}`} onClick={() => setMainImageIndex(index)}>
                <img src={image.url} alt={image.name} className="thumbnail-image" />
                <button onClick={(e) => { e.stopPropagation(); removeImage(image.id); }} className="remove-thumbnail-btn">
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="upload-area">
          <div className="upload-icon-container">
            <Camera className="upload-icon" />
          </div>
          <p className="upload-text">Upload product photos (1:1 Square)</p>
          <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="upload-input" id="photo-upload" disabled={uploading} />
          <label htmlFor="photo-upload" className={`upload-button ${uploading ? 'loading' : ''}`}>
            {uploading ? 'Uploading...' : 'Add Photos'}
          </label>
        </div>
      )}
      {uploadedImages.length > 0 && (
        <div className="upload-area">
          <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="upload-input" id="additional-photo-upload" disabled={uploading} />
          <label htmlFor="additional-photo-upload" className={`upload-button ${uploading ? 'loading' : ''}`}>
            {uploading ? 'Uploading...' : 'Add More Photos'}
          </label>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;